import { Component, OnDestroy } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { TokenService } from '../services/token.service';
import { MutationResult } from 'apollo-angular';
import { LoginV2Mutation } from '../../generated/eveportal-api/graphql';
import { environment } from '../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from '../shared/error-dialog/error-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  auditTime,
  exhaustMap,
  finalize,
  Observable,
  Subject,
  takeUntil
} from 'rxjs';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnDestroy {
  username = '';
  password = '';
  // deviceName = '';

  // Add loading state to prevent multiple submissions
  isLoading = false;

  private loginClickSubject = new Subject<void>();
  private loginClick$ = this.loginClickSubject.asObservable();
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private router: Router,
    private tokenService: TokenService,
    private dialog: MatDialog, // ✅ inject here
    private snack: MatSnackBar
  ) {
    if (
      tokenService.isRefreshTokenValid() &&
      tokenService.isEveportalJwtTokenValid()
    ) {
      this.router.navigate(['/home']);
    }

    this.loginClick$
      .pipe(
        auditTime(250), // Increased from 50ms to 250ms for better throttling
        takeUntil(this.destroy$),
        exhaustMap(() => {
          this.isLoading = true; // Set loading state
          return this.onLoginProceed().pipe(
            finalize(() => {
              this.isLoading = false;
            })
          );
        })
      )
      .subscribe({
        next: (response: MutationResult<LoginV2Mutation>) => {
          const token = response.data?.loginV2;
          if (!token) {
            this.dialog.open(ErrorDialogComponent, {
              data: { message: 'Invalid username or password.' },
            });
            return;
          }
          this.tokenService.setRefreshToken(token);
          console.log('🔑 Login successful!');
        },
        error: (err: any) => {
          console.error('❌ Login error:', err);
          const errorMessage =
            'Login failed: ' +
            (err instanceof Error ? err.message : 'Unknown error');
          this.snack.open(errorMessage, 'Dismiss');
        },
      });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onLogin(event?: Event) {
    // Prevent default form submission
    if (event) {
      event.preventDefault();
    }

    // Prevent multiple submissions if already loading
    if (this.isLoading) {
      return;
    }

    this.loginClickSubject.next();
  }

  onLoginProceed(): Observable<MutationResult> {
    let detectedEnv: 'dev' | 'beta' | 'prod' = 'prod';
    let realUsername = this.username;

    if (this.username.startsWith('dev:')) {
      detectedEnv = 'dev';
      realUsername = this.username.replace(/^dev:/, '');
    } else if (this.username.startsWith('beta:')) {
      detectedEnv = 'beta';
      realUsername = this.username.replace(/^beta:/, '');
    }

    environment.setEnvironment(detectedEnv);
    console.log('🔍 Detected environment:', detectedEnv);

    return this.authService.login(
      realUsername,
      this.password,
      environment.deviceName
    );
  }
}
